/**
 * Loan Live Assessment DTOs (Issue #1551 — WS2)
 *
 * Wire shape for `assessment.evaluateLive` — the debounced realtime AI
 * eval that previews deal signals without running the heavy persisted
 * assessment. Sibling of LoanAssessmentDto (which lives in
 * assessment.dto.ts and represents the full underwriter-twin output).
 *
 * Cached on `cases.latest_live_assessment` JSONB; freshness is determined
 * by inputsHash. The heavy assessment (factor history, score events,
 * recommendation overrides) lives in 18-digital-twin-service's own DB
 * and is unaffected by this realtime path.
 */

import { z } from 'zod';
import { LoanSizerTypeSchema } from '../enums/sizer.enums';
import { SizingFieldNameSchema } from './loan-sizer.dto';

// ============================================================================
// SIGNAL CATEGORIES
// ============================================================================

/**
 * One bullet inside a signal category. Heading + detail follow the
 * "headline + supporting sentence" pattern used by the existing
 * UnderwritingTab factor display. `field` is optional — when present, the
 * UI renders the heading as a clickable link that scrolls the named input
 * into view (reusing scrollToField on LoanOverviewPanel).
 */
export const LiveAssessmentSignalSchema = z.object({
  heading: z.string(),
  detail: z.string(),
  field: SizingFieldNameSchema.optional(),
});
export type LiveAssessmentSignal = z.infer<typeof LiveAssessmentSignalSchema>;

/**
 * Severity tier for a single suggested next step. Drives the colored
 * accent in `<InsightsRail>` so the underwriter scans the urgency at a
 * glance:
 *   - `info`    — orientation / clarification, no urgency (primary teal)
 *   - `warning` — fixable issue, intervention needed (warning amber)
 *   - `error`   — deal-breaker, must resolve to proceed (error red)
 *
 * Optional + lower-cased on the wire; consumers default to `info` when
 * absent (cached pre-severity blobs, prompt didn't emit it).
 */
export const LiveAssessmentNextStepSeveritySchema = z.enum(['info', 'warning', 'error']);
export type LiveAssessmentNextStepSeverity = z.infer<typeof LiveAssessmentNextStepSeveritySchema>;

export const LiveAssessmentNextStepSchema = z.object({
  action: z.string(),
  why: z.string(),
  severity: LiveAssessmentNextStepSeveritySchema.optional(),
});
export type LiveAssessmentNextStep = z.infer<typeof LiveAssessmentNextStepSchema>;

// ============================================================================
// FULL DTO
// ============================================================================

export const LoanLiveAssessmentSchema = z.object({
  /** ISO-8601 generation timestamp. Set server-side. */
  generatedAt: z.string(),
  /** SHA256 of canonical inputs. Used by the cache short-circuit on cases.latest_live_assessment. */
  inputsHash: z.string().min(1),
  signals: z.object({
    /** Structural strength of the deal. */
    positive: z.array(LiveAssessmentSignalSchema),
    /** Yellow-flag items worth watching. */
    cautions: z.array(LiveAssessmentSignalSchema),
    /** Red-flag items requiring intervention. */
    warnings: z.array(LiveAssessmentSignalSchema),
    /** Deal-breakers (above-cap leverage, ARV ≤ purchase, sub-floor FICO, etc.). */
    errors: z.array(LiveAssessmentSignalSchema),
  }),
  nextSteps: z.array(LiveAssessmentNextStepSchema),
  /** 1-2 sentence Gemini summary. */
  overallAssessment: z.string(),
});
export type LoanLiveAssessmentDto = z.infer<typeof LoanLiveAssessmentSchema>;

// ============================================================================
// REQUEST INPUTS — what the realtime path sends to evaluateLive
// ============================================================================

/**
 * The unsaved sizing inputs that drive the live eval. These are exactly
 * the fields included in the canonical inputsHash (purchase, ARV, rehab,
 * requested loan, rate, points, term, FICO, tier, loan type, address).
 * UI presentation fields (scenario name, narrative text, panel state)
 * are deliberately excluded so they don't bust the cache.
 *
 * Unit convention: `rate` and `points` are in PERCENT (e.g., 11 for 11%,
 * 2 for 2 points) — matches the field-rules engine, the EvaluateLive
 * prompt's `${rate}%` formatter, and the API gateway tests. Callers that
 * store the canonical decimal form (e.g., 0.11) must convert before
 * submitting.
 */
export const EvaluateLiveInputsSchema = z.object({
  purchase: z.number(),
  rehabBudget: z.number(),
  arv: z.number(),
  requestedLoan: z.number(),
  rate: z.number(),
  points: z.number(),
  termMonths: z.number().int(),
  fico: z.number().int(),
  tier: z.number().int(),
  // loanType + address are interpolated into the Gemini prompt at
  // 18-digital-twin-service/.../evaluate-live-prompt.ts. Bounding them
  // both shape and length keeps a malicious borrower from smuggling
  // prompt-injection text through the deal-review path.
  loanType: LoanSizerTypeSchema,
  address: z.string().max(500),
});
export type EvaluateLiveInputs = z.infer<typeof EvaluateLiveInputsSchema>;

/**
 * Computed metrics passed alongside inputs so the LLM doesn't have to
 * redo the deterministic math. NOT part of inputsHash — these are
 * derivable from the other inputs.
 */
export const EvaluateLiveMetricsSchema = z.object({
  /** Loan ÷ purchase (LTC). */
  ltc: z.number(),
  /** Loan ÷ ARV (ARLTV). */
  arltv: z.number(),
  /** ARV − (purchase + rehab) — equity headroom in dollars. */
  headroom: z.number(),
});
export type EvaluateLiveMetrics = z.infer<typeof EvaluateLiveMetricsSchema>;
